// src/components/CarFilterBar.tsx

import {
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Slider,
  Stack,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RightDrawer from './RightDrawer';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { RangeFilterMenu } from './RangeFilterMenu';
import axiosInstance from '../services/axiosInstance';
// ✅ Import Encoder/Decoder
import { encodeId, decodeId } from '../utils/idEncoder';

type CarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  onFilterChange?: (key: string, value: string) => void;
};

// Define keys that should be encrypted
const ENCRYPTED_KEYS = [
    'vehicleBrandIds',
    'vehicleBodyTypeIds',
    'vehicleFuelTypeIds',
    'vehicleTransmissionTypeIds',
    'vehicleClassIds',
    'dealerIds'
];

interface ApiDataResponse {
    responseCode: string;
    responseData?: {
        dealerCompanyData?: any[];
        vehicleBodyTypeData?: any[];
        vehicleFuelTypeData?: any[];
        vehicleTransmissionTypeData?: any[];
        vehicleBrandData?: any[];
    }
}

const CarFilterBar = ({
  viewMode,
  onToggleViewMode,
  searchQuery,
  onSearchChange,
  url,
  onFilterChange,
}: CarFilterBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- STATE ---
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [TransmissionTypeData, setvehicleTransmissionTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  
  // Temp state for checkboxes
  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortList, setSortList] = useState<{ key: string; direction: 'asc' | 'desc' }[]>([]);

  // --- SORTING LOGIC ---
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortChange = (key: string) => {
    setSortList((prev) => {
      const existing = prev.find((s) => s.key === key);
      let updated: { key: string; direction: 'asc' | 'desc' }[]; 
      if (existing) {
        const newDirection: 'asc' | 'desc' = existing.direction === 'asc' ? 'desc' : 'asc';
        updated = prev.map((s) => s.key === key ? { ...s, direction: newDirection } : s);
      } else {
        updated = [...prev, { key, direction: 'asc' }];
      }
      const params = new URLSearchParams(location.search);
      params.set('sortKeys', updated.map((s) => s.key).join(','));
      params.set('sortDirs', updated.map((s) => s.direction).join(','));
      params.delete('page');
      navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
      return updated;
    });
    handleSortClose();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keys = params.get('sortKeys')?.split(',') || [];
    const dirs = params.get('sortDirs')?.split(',') || [];
    if (keys.length > 0) {
      setSortList(keys.map((key, i) => ({ key, direction: (dirs[i] === 'desc' ? 'desc' : 'asc') })));
    }
  }, [location.search]);

  // --- YEAR LOGIC ---
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 15;
  const maxYear = currentYear;
  const searchParams = new URLSearchParams(location.search);
  const [yearRange, setYearRange] = useState<number[]>([
    Number(searchParams.get('minManufacturingYear')) || minYear,
    Number(searchParams.get('maxManufacturingYear')) || maxYear,
  ]);
  const yearMarks = Array.from({ length: 6 }, (_, i) => ({ value: minYear + i * 3, label: (minYear + i * 3).toString() }));

  const handleYearChange = (_: any, newValue: number | number[]) => setYearRange(newValue as number[]);

  const applyYearFilter = () => {
    const [min, max] = yearRange;
    const params = new URLSearchParams(location.search);
    params.set('minManufacturingYear', min.toString());
    params.set('maxManufacturingYear', max.toString());
    params.delete('page');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    handleClose();
  };

  const clearYearFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete('minManufacturingYear');
    params.delete('maxManufacturingYear');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    setYearRange([minYear, maxYear]);
    handleClose();
  };

  // --- API DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = { dealerCompanyData: true, vehicleBodyTypeData: true, vehicleFuelTypeData: true, vehicleTransmissionTypeData: true, vehicleBrandData: true };
        const res = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", payload);
        const data = res.data as ApiDataResponse;
        if (data.responseCode === "00" && data.responseData) {
          setvehicleBodyTypeData(data.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(data.responseData.vehicleFuelTypeData || []);
          setvehicleTransmissionTypeData(data.responseData.vehicleTransmissionTypeData || []);
          setvehicleBrandData(data.responseData.vehicleBrandData || []);
          setDealerData(data.responseData.dealerCompanyData || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  // --- UI HELPERS ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setSelectedLabel(filter);
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => { setAnchorEl(null); setActiveFilter(null); };

  // --- CHECKBOX LOGIC (UPDATED) ---
  const getTempSelected = (key: string): string[] => {
    if (!initializedKeys.has(key)) {
      const params = new URLSearchParams(location.search);
      const val = params.get(key);
      const parts = val ? val.split(',') : [];

      // ✅ DECODE URL hashes back to IDs for the internal checkbox state
      let decodedIds = parts;
      if (ENCRYPTED_KEYS.includes(key)) {
          decodedIds = parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
      }
      
      setTempSelected(prev => ({ ...prev, [key]: decodedIds }));
      setInitializedKeys(prev => new Set(prev).add(key));
      return decodedIds;
    }
    return tempSelected[key] || [];
  };

  const toggleSelection = (key: string, id: string) => {
    setTempSelected(prev => {
      const set = new Set(prev[key] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  // ✅ ENCODE IDs before sending to URL
  const applySelection = (key: string) => {
    const rawIds = tempSelected[key] || [];
    let outputString = "";

    if (ENCRYPTED_KEYS.includes(key)) {
        // Convert "1" -> "X9"
        const encoded = rawIds.map(id => encodeId(id));
        outputString = encoded.join(',');
    } else {
        outputString = rawIds.join(',');
    }

    if (onFilterChange) onFilterChange(key, outputString);
    handleClose();
  };

  const clearSelection = (key: string) => {
    setTempSelected(prev => ({ ...prev, [key]: [] }));
    if (onFilterChange) onFilterChange(key, '');
    handleClose();
  };

  const renderCheckboxFilterMenu = (key: string, data: any[], idField: string, labelField: string) => (
    <Box sx={{ width: 220, maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, px: 1, py: 1, overflowY: 'auto' }}>
        {data.map((item) => {
          const id = item[idField].toString();
          // Checkboxes use the Raw ID (e.g. "1")
          const checked = getTempSelected(key).includes(id);
          return (
            <MenuItem key={id} onClick={() => toggleSelection(key, id)} disableRipple>
              <input type="checkbox" checked={checked} readOnly style={{ marginRight: 8 }} />
              <Typography>{item[labelField]}</Typography>
            </MenuItem>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid #eee', p: 1, position: 'sticky', bottom: 0, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => clearSelection(key)}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)}>Apply</Button>
        </Stack>
      </Box>
    </Box>
  );

  const filterButtons = ['Brand & Model', 'Body Type', 'Fuel Type', 'Transmission', 'Vehicle Status', 'Car Price & Instalment', 'Year', 'Mileage', 'Depreciation'];
  const filterMap: any = {
    'Body Type': { key: 'vehicleBodyTypeIds', data: bodyTypesData, idField: 'vehicleBodyTypeId' },
    'Brand & Model': { key: 'vehicleBrandIds', data: brandData, idField: 'vehicleBrandId' },
    'Fuel Type': { key: 'vehicleFuelTypeIds', data: fuelTypeData, idField: 'vehicleFuelTypeId' },
    'Transmission': { key: 'vehicleTransmissionTypeIds', data: TransmissionTypeData, idField: 'vehicleTransmissionTypeId' },
    // Status is string code, do not encrypt
    'Vehicle Status': { key: 'statusId', data: [{ statusId: 'VPB', description: 'Available' }, { statusId: 'VSL', description: 'Sold' }], idField: 'statusId' },
    'Car Price & Instalment': { key: 'minAskingPrice', getLabel: (p: URLSearchParams) => p.get('minAskingPrice') ? `$${Number(p.get('minAskingPrice')).toLocaleString()}+` : null },
    'Year': { key: 'minManufacturingYear', getLabel: (p: URLSearchParams) => p.get('minManufacturingYear') ? `${p.get('minManufacturingYear')}+` : null },
    'Mileage': { key: 'minMileage', getLabel: (p: URLSearchParams) => p.get('minMileage') ? `${Number(p.get('minMileage')).toLocaleString()}km+` : null },
    'Depreciation': { key: 'minDepreciation', getLabel: (p: URLSearchParams) => p.get('minDepreciation') ? `$${Number(p.get('minDepreciation')).toLocaleString()}+` : null },
  };

  return (
    <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
      {/* Search Bar */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          fullWidth placeholder="Search..." value={searchQuery} onChange={(e) => onSearchChange(e.target.value)}
          size="small" sx={{ bgcolor: '#fff' }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: searchQuery && <IconButton size="small" onClick={() => { onSearchChange(''); navigate(url || '/vehicle-listing'); }}><ClearIcon fontSize="small" /></IconButton>
          }}
        />
        <Button variant="contained" onClick={() => navigate(`${location.pathname}?vehicleModel=${searchQuery}`)}>Search</Button>
        <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={() => { onSearchChange(''); navigate(url || '/vehicle-listing'); }}>Reset</Button>
      </Stack>

      {/* Filter Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
        {onToggleViewMode && <IconButton onClick={onToggleViewMode} sx={{ bgcolor: '#fff', borderRadius: 1 }}>{viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}</IconButton>}
        {filterButtons.map(label => {
          const conf = filterMap[label];
          let subLabel = null;
          let variant: 'outlined' | 'contained' = 'outlined';
          if (conf) {
             const params = new URLSearchParams(location.search);
             if (conf.getLabel) subLabel = conf.getLabel(params);
             else {
                // To show count, we just check presence of param
                const count = (params.get(conf.key)?.split(',') || []).filter(Boolean).length;
                if (count > 0) subLabel = `${count} selected`;
             }
             if (subLabel) variant = 'contained';
          }
          const active = (selectedLabel === label && anchorEl) || !!subLabel;
          return (
            <Button key={label} variant={variant} size="small" onClick={(e) => handleClick(e, label)} sx={{ borderRadius: 5, whiteSpace: 'nowrap', bgcolor: active ? '#FFA100' : '#fff', color: active ? '#000' : '#333', borderColor: active ? 'transparent' : '#e0e0e0', '&:hover': { bgcolor: '#FFA100' } }}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="body2" fontWeight={600}>{label}</Typography>
                {subLabel && <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{subLabel}</Typography>}
              </Box>
            </Button>
          );
        })}
      </Box>

      {/* Utilities */}
      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
        <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={() => setDrawerOpen(true)}>All Filters</Button>
        <Button variant="outlined" size="small" startIcon={<SortIcon />} onClick={handleSortClick}>{sortList.length ? `Sorted (${sortList.length})` : 'Sort'}</Button>
        <Button variant="text" size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => { setSortList([]); navigate(url || '/vehicle-listing'); }} sx={{ ml: 'auto' }}>Clear All Filters</Button>
      </Box>

      {/* Menus */}
      <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={handleSortClose}>
        {['askingPrice', 'addDate', 'mileage', 'vehicleModel'].map(k => (
          <MenuItem key={k} onClick={() => handleSortChange(k)}>{k} {sortList.find(s => s.key === k) && (sortList.find(s => s.key === k)?.direction === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}</MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} PaperProps={{ sx: { width: 250, maxHeight: 300, mt: 1 } }}>
        {activeFilter === 'Brand & Model' && renderCheckboxFilterMenu('vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
        {activeFilter === 'Body Type' && renderCheckboxFilterMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
        {activeFilter === 'Fuel Type' && renderCheckboxFilterMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
        {activeFilter === 'Transmission' && renderCheckboxFilterMenu('vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
        {activeFilter === 'Vehicle Status' && renderCheckboxFilterMenu('statusId', [{ statusId: 'VPB', description: 'Available' }, { statusId: 'VSL', description: 'Sold' }], 'statusId', 'description')}
        {activeFilter === 'Year' && (
           <Box p={2}>
             <Typography gutterBottom>Year Range</Typography>
             <Slider value={yearRange} onChange={handleYearChange} valueLabelDisplay="auto" min={minYear} max={maxYear} marks={yearMarks} />
             <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
               <Button size="small" onClick={clearYearFilter}>Clear</Button>
               <Button size="small" variant="contained" onClick={applyYearFilter}>Apply</Button>
             </Stack>
           </Box>
        )}
      </Menu>

      {/* External Components */}
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Car Price & Instalment" minParamKey="minAskingPrice" maxParamKey="maxAskingPrice" min={1000} max={9999999} label="Price Range ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Depreciation" minParamKey="minDepreciation" maxParamKey="maxDepreciation" min={1000} max={100000} label="Depreciation ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Mileage" minParamKey="minMileage" maxParamKey="maxMileage" min={0} max={300000} label="Mileage (km)" onClose={handleClose} />
      
      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fuelTypeData={fuelTypeData} TransmissionTypeData={TransmissionTypeData} bodyTypesData={bodyTypesData} brandData={brandData} dealerData={dealerData} />
    </Box>
  );
};

export default CarFilterBar;